import * as Yup from 'yup';
import { useMemo } from 'react';

import useAdditionalFields from './useAdditionalFields';
import { AI_STUDIO_FIELDS } from '../../../constants';

const useFormValidation = () => {
  const additionalFields = useAdditionalFields();

  const validation = useMemo(() => {
    return actionsBlockValidation(additionalFields);
  }, [additionalFields]);

  return validation;
};

const isFieldVisible = (field, values) => {
  if (!field.showWhen) {
    return true;
  }

  return values[field.showWhen.field] === field.showWhen.value;
};

const actionsBlockValidation = (additionalFields = {}) => {
  return Yup.lazy((values = {}) => {
    const selectedAction = values.action;
    const fieldsForSelectedAction = additionalFields[selectedAction] ?? [];

    const dynamicValidation = fieldsForSelectedAction.reduce((acc, field) => {
      if (!isFieldVisible(field, values)) {
        return acc;
      }

      const fieldValidation = getFieldValidation(field);

      if (!fieldValidation) {
        return acc;
      }

      return {
        ...acc,
        ...fieldValidation,
      };
    }, {});

    return Yup.object().shape({
      action: Yup.string().required('required'),
      ...dynamicValidation,
    });
  });
};

const requiredStringValidation = Yup.string().trim().required('required');

const positiveNumberValidation = Yup.string()
  .trim()
  .required('required')
  .test('positive-number', 'required', (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0;
  });

const getFieldValidation = (field) => {
  if (field.type === AI_STUDIO_FIELDS.CHAT) {
    const validation = {};

    if (field.isRequired) {
      validation[field.name] = requiredStringValidation;
    }

    if (field.isFileRequired) {
      validation.attachments = Yup.array().min(1, 'fileRequired');
    }

    return Object.keys(validation).length ? validation : null;
  }

  if (!field?.isRequired) {
    return null;
  }

  if (field.type === AI_STUDIO_FIELDS.FILE) {
    return { [field.name]: Yup.mixed().nullable().required('required') };
  }

  if (field.type === AI_STUDIO_FIELDS.CONVERT) {
    return {
      [`${field.name}_from`]: requiredStringValidation,
      [`${field.name}_to`]: requiredStringValidation,
    };
  }

  if (field.name === 'width' || field.name === 'height') {
    return { [field.name]: positiveNumberValidation };
  }

  return { [field.name]: requiredStringValidation };
};

export default useFormValidation;
